/**
 * Storage migrations (1.1.0): pieces the wizard, the detail page and the list share. The limits and window fields
 * (the wizard's step 4, and the detail page's editor while running), the dry-run report (the review step and the
 * detail page), the typed-name field for Start and Remove sources, progress bars, and a confirmation dialog.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  DEFAULT_MIGRATION_LIMITS, MIGRATION_FILE_KINDS, MigrationLimits,
  type MigrationCountsDTO, type MigrationDTO, type MigrationItemReason, type MigrationPlanReport, type MigrationWindowEnd, type TransferWindow,
} from '@prism/contracts';
import { Modal } from '../../../components/Modal';
import { Segmented } from '../../../components/Segmented';
import { bytes, dateTime } from '../../../lib/format';
import { TransferWindowPicker } from './TransferWindows';
import { FILE_KIND_LABEL, ITEM_REASON_LABEL, WARNING_TITLE, finishLine, parseBackoff, progressPct } from './migrationText';

// ---------------------------------------------------------------- limits

/** The limits as the form holds them: text, so a field can be empty (none) while typing. */
export interface LimitsDraft {
  maxConcurrent: string;
  /** MB/s for the whole migration; empty: none. */
  bandwidth: string;
  filesPerDay: string;
  /** GB (10^9 bytes) a day; empty: none. */
  gbPerDay: string;
  stopAtPct: string;
  retryAttempts: string;
  /** "5, 30, 120" minutes. */
  backoff: string;
  errorPausePct: string;
  windowEnd: MigrationWindowEnd;
}

const str = (n: number | null | undefined) => (n == null ? '' : String(n));
const num = (s: string) => (s.trim() === '' ? null : Number(s));

export function limitsDraft(l: MigrationLimits = { ...DEFAULT_MIGRATION_LIMITS, retry: { ...DEFAULT_MIGRATION_LIMITS.retry, backoffMinutes: [...DEFAULT_MIGRATION_LIMITS.retry.backoffMinutes] } }): LimitsDraft {
  return {
    maxConcurrent: String(l.maxConcurrent),
    bandwidth: str(l.maxBandwidthMBps),
    filesPerDay: str(l.maxFilesPerDay),
    gbPerDay: l.maxBytesPerDay == null ? '' : String(Math.round((l.maxBytesPerDay / 1e9) * 100) / 100),
    stopAtPct: str(l.stopAtDestPct),
    retryAttempts: String(l.retry.attempts),
    backoff: l.retry.backoffMinutes.join(', '),
    errorPausePct: String(l.errorPausePct),
    windowEnd: l.windowEnd,
  };
}

/** The limits to send (numbers may be NaN while a field holds text: limitsProblem says so first). */
export function limitsFromDraft(d: LimitsDraft): MigrationLimits {
  const gb = num(d.gbPerDay);
  return {
    maxConcurrent: Number(d.maxConcurrent),
    maxBandwidthMBps: num(d.bandwidth),
    maxFilesPerDay: num(d.filesPerDay),
    maxBytesPerDay: gb == null ? null : Math.round(gb * 1e9),
    stopAtDestPct: num(d.stopAtPct),
    retry: { attempts: Number(d.retryAttempts), backoffMinutes: parseBackoff(d.backoff) ?? [] },
    errorPausePct: Number(d.errorPausePct),
    windowEnd: d.windowEnd,
  };
}

const LIMIT_LABEL: Record<string, string> = {
  maxConcurrent: 'Transfers at once', maxBandwidthMBps: 'Bandwidth', maxFilesPerDay: 'Files a day', maxBytesPerDay: 'GB a day',
  stopAtDestPct: 'Stop at', retry: 'Retries', errorPausePct: 'Pause on errors', windowEnd: 'When the window closes',
};

/** The first problem with the limits, as "Field: problem", or null. */
export function limitsProblem(d: LimitsDraft): string | null {
  if (parseBackoff(d.backoff) == null) return 'Retries: give the pauses as minutes, such as 5, 30, 120';
  const r = MigrationLimits.safeParse(limitsFromDraft(d));
  if (r.success) return null;
  const i = r.error.issues[0]!;
  return `${LIMIT_LABEL[String(i.path[0])] ?? String(i.path[0] ?? 'Limits')}: ${i.message}`;
}

function NumField({ id, label, value, onChange, unit, help, placeholder, min, step }: {
  id: string; label: string; value: string; onChange: (v: string) => void; unit?: string; help?: ReactNode; placeholder?: string; min?: number; step?: number;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="mig-unit"><input id={id} className="input" type="number" inputMode="decimal" min={min} step={step} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={id} />{unit && <span className="faint">{unit}</span>}</div>
      {help && <span className="help">{help}</span>}
    </div>
  );
}

/**
 * When and how fast: the window, transfers at once (with what PRISM admits at most), bandwidth, daily caps, the
 * destination fill stop, retries, the error pause and what happens to running transfers when the window closes.
 */
export function LimitsFields({ value: d, onChange, windowId, onWindow, transferSlots, idPrefix = 'mig' }: {
  value: LimitsDraft; onChange: (d: LimitsDraft) => void;
  windowId: string | null; onWindow: (id: string | null) => void;
  /** The workflow service's transfer admission (at most this many transfers run at once across PRISM). */
  transferSlots?: number | null;
  idPrefix?: string;
}) {
  const set = <K extends keyof LimitsDraft>(k: K, v: LimitsDraft[K]) => onChange({ ...d, [k]: v });
  const perTransfer = num(d.bandwidth) != null && Number(d.maxConcurrent) > 0 ? Number(d.bandwidth) / Number(d.maxConcurrent) : null;
  return (
    <div className="mig-limits">
      <div className="field">
        <label htmlFor={`${idPrefix}-window`}>When</label>
        <TransferWindowPicker id={`${idPrefix}-window`} value={windowId} onChange={onWindow} allowCreate />
        <span className="help">Files move only while the window is open. Any time: whenever the migration runs.</span>
      </div>
      <div className="field-row">
        <NumField id={`${idPrefix}-concurrent`} label="Transfers at once" value={d.maxConcurrent} min={1} onChange={(v) => set('maxConcurrent', v)}
          help={transferSlots ? `PRISM runs at most ${transferSlots} transfers at once across everything; a migration takes its share of those slots.` : 'From 1 to 64.'} />
        <NumField id={`${idPrefix}-bandwidth`} label="Bandwidth" unit="MB/s" placeholder="No limit" min={1} value={d.bandwidth} onChange={(v) => set('bandwidth', v)}
          help={perTransfer != null && Number.isFinite(perTransfer) ? `Shared by the transfers: ${Math.round(perTransfer * 10) / 10} MB/s each. Copies inside one object store are not limited.` : 'Shared by the transfers, at least 1 MB/s each. Copies inside one object store are not limited.'} />
      </div>
      <div className="field-row three">
        <NumField id={`${idPrefix}-files-day`} label="Files a day" placeholder="No limit" min={1} value={d.filesPerDay} onChange={(v) => set('filesPerDay', v)} />
        <NumField id={`${idPrefix}-gb-day`} label="Data a day" unit="GB" placeholder="No limit" min={0.01} step={0.01} value={d.gbPerDay} onChange={(v) => set('gbPerDay', v)} />
        <NumField id={`${idPrefix}-stop-at`} label="Stop when the destination is" unit="% full" placeholder="Full" min={1} value={d.stopAtPct} onChange={(v) => set('stopAtPct', v)} />
      </div>
      <div className="field-row three">
        <NumField id={`${idPrefix}-retries`} label="Retries per file" min={0} value={d.retryAttempts} onChange={(v) => set('retryAttempts', v)} />
        <div className="field">
          <label htmlFor={`${idPrefix}-backoff`}>Pause before each retry</label>
          <div className="mig-unit"><input id={`${idPrefix}-backoff`} className="input" value={d.backoff} onChange={(e) => set('backoff', e.target.value)} data-testid={`${idPrefix}-backoff`} /><span className="faint">min</span></div>
          <span className="help">The last pause repeats.</span>
        </div>
        <NumField id={`${idPrefix}-error-pause`} label="Pause when failures reach" unit="%" min={1} value={d.errorPausePct} onChange={(v) => set('errorPausePct', v)} help="Of the last 100 files (after at least 20)." />
      </div>
      <div className="field">
        <label>When the window closes</label>
        <Segmented role="radiogroup" label="When the window closes" buttonType="button" value={d.windowEnd} onChange={(v) => set('windowEnd', v)} options={[
          { value: 'finish_running', label: 'Let running transfers finish', testId: `${idPrefix}-window-end-finish` },
          { value: 'cancel_running', label: 'Cancel running transfers', testId: `${idPrefix}-window-end-cancel` },
        ]} />
        <span className="help">Queued transfers are always cancelled at closing; cancelled files are queued again when the window opens.</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the dry run

const files = (n: number) => `${n.toLocaleString('en-US')} file${n === 1 ? '' : 's'}`;
export const countsLabel = (c: MigrationCountsDTO) => `${files(c.files)} · ${bytes(c.bytes)}`;

/** Counts per kind and skip reasons, for the live preview and the report. */
export function KindCounts({ byKind, skipped }: { byKind: Record<string, MigrationCountsDTO>; skipped: Partial<Record<MigrationItemReason, MigrationCountsDTO>> }) {
  const skips = Object.entries(skipped) as Array<[MigrationItemReason, MigrationCountsDTO]>;
  return (
    <table className="table compact mig-table" data-testid="mig-kind-counts">
      <tbody>
        {MIGRATION_FILE_KINDS.map((k) => byKind[k] && <tr key={k}><td>{FILE_KIND_LABEL[k]}</td><td className="num">{byKind[k]!.files.toLocaleString('en-US')}</td><td className="num">{bytes(byKind[k]!.bytes)}</td></tr>)}
        {skips.map(([r, c]) => <tr key={r} className="mig-skip" data-testid="mig-skip-row"><td className="wrap">Skipped: {ITEM_REASON_LABEL[r] ?? r}</td><td className="num">{c.files.toLocaleString('en-US')}</td><td className="num">{bytes(c.bytes)}</td></tr>)}
      </tbody>
    </table>
  );
}

/** The dry run: what moves, skip reasons, the largest files, destination room, the estimate and the warnings. */
export function PlanReportView({ report: r, window: w = null }: { report: MigrationPlanReport; /** The window the plan was made for (its zone and hours). */ window?: TransferWindow | null }) {
  const finish = finishLine(r, w);
  return (
    <div className="mig-report" data-testid="mig-plan-report">
      <div className="mig-stats">
        <div><span className="mig-stat-value" data-testid="mig-plan-files">{r.files.toLocaleString('en-US')}</span><span className="mig-stat-label">files</span></div>
        <div><span className="mig-stat-value">{bytes(r.bytes)}</span><span className="mig-stat-label">to move</span></div>
        <div><span className="mig-stat-value" data-testid="mig-plan-estimate">{r.estimate.description ?? 'Unknown'}</span><span className="mig-stat-label" data-testid="mig-plan-finish">{finish ?? 'estimated time'}</span></div>
        <div>
          <span className={`mig-stat-value ${r.destination.enough === false ? 'danger' : ''}`} data-testid="mig-plan-room">{r.destination.freeBytes == null ? 'Unknown' : bytes(r.destination.freeBytes)}</span>
          <span className="mig-stat-label">{r.destination.enough === false ? 'free at the destination: not enough' : r.destination.enough ? 'free at the destination: enough' : 'free at the destination'}</span>
        </div>
      </div>
      {r.warnings.length > 0 && (
        <div className="mig-warnings" data-testid="mig-warnings">
          {r.warnings.map((w) => (
            <div className="banner warning" key={w.code} data-code={w.code}><AlertTriangle size={15} /><div className="banner-body">
              <strong>{WARNING_TITLE[w.code] ?? w.code}</strong><span>{w.message}</span>
              {w.rules?.length ? <span className="faint">{w.rules.map((x) => x.name).join(', ')}</span> : null}
            </div></div>
          ))}
        </div>
      )}
      {r.warnings.length === 0 && <div className="mig-ok"><CheckCircle2 size={15} /> Nothing to warn about.</div>}
      <div className="mig-report-cols">
        <div>
          <p className="section-label">By kind</p>
          <KindCounts byKind={r.byKind} skipped={r.skipped} />
        </div>
        <div>
          <p className="section-label">Largest files</p>
          {r.largest.length === 0 ? <p className="faint" style={{ margin: 0 }}>None</p> : (
            <table className="table compact mig-table" data-testid="mig-largest"><tbody>
              {r.largest.slice(0, 8).map((f) => <tr key={f.objectKey}><td className="mono wrap" title={f.objectKey}>{f.objectKey.split('/').pop()}</td><td className="faint">{FILE_KIND_LABEL[f.fileKind]}</td><td className="num">{bytes(f.sizeBytes)}</td></tr>)}
            </tbody></table>
          )}
        </div>
      </div>
      <span className="help">Planned {dateTime(r.plannedAt)}. Files added to the source after planning are not part of this migration.</span>
    </div>
  );
}

// ---------------------------------------------------------------- small pieces

/** Files and bytes bars with their counts. */
export function MigrationBars({ m, compact = false }: { m: Pick<MigrationDTO, 'counters'>; compact?: boolean }) {
  const c = m.counters;
  const finishedFiles = c.done.files + c.failed.files + c.skipped.files;
  const finishedBytes = c.done.bytes + c.failed.bytes + c.skipped.bytes;
  return (
    <div className={`mig-bars ${compact ? 'compact' : ''}`} data-testid="mig-bars">
      <div className="mig-bar"><div className="progress"><span style={{ width: `${progressPct(m, 'files')}%` }} /></div>
        <span className="faint">{finishedFiles.toLocaleString('en-US')} of {c.total.files.toLocaleString('en-US')} files</span></div>
      <div className="mig-bar"><div className="progress"><span style={{ width: `${progressPct(m, 'bytes')}%` }} /></div>
        <span className="faint">{bytes(finishedBytes)} of {bytes(c.total.bytes)}</span></div>
    </div>
  );
}

/** "Type <name> to confirm": Start (a move) and Remove sources. */
export function TypedName({ name, value, onChange, id = 'mig-confirm-name', error }: { name: string; value: string; onChange: (v: string) => void; id?: string; error?: string | null }) {
  return (
    <div className="field">
      <label htmlFor={id}>Type <strong>{name}</strong>, the source location, to confirm</label>
      <input id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" spellCheck={false} data-testid={id} />
      {error && <span className="error-text" data-testid={`${id}-error`}>{error}</span>}
    </div>
  );
}

/** A confirmation with its own button text, for Cancel, Delete and Pause. */
export function ConfirmDialog({ title, children, confirm, danger = false, busy = false, disabled = false, onConfirm, onClose, testId }: {
  title: string; children: ReactNode; confirm: string; danger?: boolean; busy?: boolean; disabled?: boolean; onConfirm: () => void; onClose: () => void; testId?: string;
}) {
  return (
    <Modal title={title} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Back</button>
      <button className={`btn ${danger ? 'danger' : 'primary'}`} disabled={busy || disabled} onClick={onConfirm} data-testid={testId}>{confirm}</button>
    </>}>
      {children}
    </Modal>
  );
}
